import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionIcon, Box, Flex, Group, Paper, Stepper, Text } from "@mantine/core";
import { IconCheck, IconClockPause, IconDots, IconEye, IconEyeOff } from "@tabler/icons-react";
import dayjs from "dayjs";
import { CampaignMilestone, CampaignState } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { campaignStateWeights } from "../../../../../hooks/campaigns";

export interface TimelineWidgetProps {
    campaign: CampaignQueryDataType;
}

export const TimelineWidget = ({ campaign }: TimelineWidgetProps) => {
    const { t } = useTranslation(["common"]);

    const [showIcons, setShowIcons] = useState(
        localStorage.getItem("__campaigndetail_timelinewidget__showicons") === "f" ? false : true
    );

    const toggleShowIcons = useCallback(() => {
        setShowIcons(!showIcons);
        localStorage.setItem("__campaigndetail_timelinewidget__showicons", !showIcons ? "t" : "f");
    }, [showIcons]);

    const { activeStep, steps } = useMemo(() => {
        const milestonesMap = (campaign?.milestones || []).reduce<Record<string, CampaignMilestone>>(
            (map, milestone) => ({
                ...map,
                [milestone.type]: milestone,
            }),
            {}
        );

        let activeStep = 0;
        let steps: {
            label: ReactNode;
            description: ReactNode;
            icon?: ReactNode;
        }[] = [];

        if (milestonesMap[`state:${CampaignState.Draft}`]?.timestamp) {
            steps.push({
                label: "Založeno",
                description: dayjs(milestonesMap[`state:${CampaignState.Draft}`].timestamp).format("DD.MM.YYYY"),
            });
        }

        if (campaign?.state === CampaignState.Draft) {
            activeStep = steps.length - 1;
        }

        if (campaignStateWeights[campaign?.state] > campaignStateWeights[CampaignState.Draft]) {
            activeStep = 0;
            steps = [];
        }

        if (milestonesMap[`state:${CampaignState.Approved}`]?.timestamp) {
            steps.push({
                label: "Schváleno",
                description: dayjs(milestonesMap[`state:${CampaignState.Approved}`].timestamp).format("DD.MM.YYYY"),
            });

            if (campaign?.state === CampaignState.Approved) {
                activeStep = steps.length - 1;
            }
        } else if (milestonesMap[`state:${CampaignState.Publishable}`]?.timestamp) {
            steps.push({
                label: "Čeká na schválení",
                description: dayjs(milestonesMap[`state:${CampaignState.Publishable}`].timestamp).format("DD.MM.YYYY"),
            });

            if (campaign?.state === CampaignState.Publishable) {
                activeStep = steps.length - 1;
            }

            steps.push({
                label: "Schváleno",
                description: `Nejpozději ${dayjs(campaign?.settings?.openAt).format("DD.MM.YYYY")}`,
            });
        }

        if (milestonesMap[`state:${CampaignState.Published}`]?.timestamp) {
            steps.push({
                label: "Zveřejněno",
                description: dayjs(milestonesMap[`state:${CampaignState.Published}`].timestamp).format("DD.MM.YYYY"),
            });
        }

        if (campaign?.state === CampaignState.Published) {
            activeStep = steps.length - 1;
        }

        let openedDescription: ReactNode = dayjs(campaign?.settings?.openAt).format("DD.MM.YYYY");

        if (milestonesMap[`state:${CampaignState.Opened}`]?.timestamp) {
            openedDescription = (
                <>
                    <Box>
                        <Text size="80%" style={{ display: "inline", fontFamily: "monospace" }}>
                            Očk.
                        </Text>{" "}
                        {dayjs(campaign?.settings?.openAt).format("DD.MM.YYYY")}
                    </Box>
                    <Box mt="2px">
                        <Text size="80%" style={{ display: "inline", fontFamily: "monospace" }}>
                            Akt.
                        </Text>{" "}
                        {dayjs(milestonesMap[`state:${CampaignState.Opened}`].timestamp).format("DD.MM.YYYY")}
                    </Box>
                </>
            );
        }

        steps.push({
            label: "Přihlašování",
            description: openedDescription,
        });

        if (campaign?.state === CampaignState.Opened) {
            activeStep = steps.length - 1;
        }

        let closedDescription: ReactNode = dayjs(campaign?.settings?.closeAt).format("DD.MM.YYYY");

        if (milestonesMap[`state:${CampaignState.Closed}`]?.timestamp) {
            closedDescription = (
                <>
                    <Box>
                        <Text size="80%" style={{ display: "inline", fontFamily: "monospace" }}>
                            Očk.
                        </Text>{" "}
                        {dayjs(campaign?.settings?.closeAt).format("DD.MM.YYYY")}
                    </Box>
                    <Box mt="2px">
                        <Text size="80%" style={{ display: "inline", fontFamily: "monospace" }}>
                            Akt.
                        </Text>{" "}
                        {dayjs(milestonesMap[`state:${CampaignState.Closed}`].timestamp).format("DD.MM.YYYY")}
                    </Box>
                </>
            );
        }

        steps.push({
            label: "Přihlašování uzavřeno",
            description: closedDescription,
        });

        if (campaign?.state === CampaignState.Closed) {
            activeStep = steps.length - 1;
        }

        steps.push({
            label: t("campaign_timeline_submissionperioddays", {
                count: campaign?.settings?.submissionPeriodDays || 0,
            }),
            description: (
                <>
                    <Text size="80%" style={{ display: "inline", fontFamily: "monospace" }}>
                        Očk.
                    </Text>{" "}
                    {dayjs(campaign?.settings?.closeAt)
                        .add(campaign?.settings?.submissionPeriodDays, "days")
                        .format("DD.MM.YYYY")}
                </>
            ),
        });

        steps.push({
            label: "Kampaň ukončena",
            description: milestonesMap[`state:${CampaignState.Finished}`]?.timestamp
                ? dayjs(milestonesMap[`state:${CampaignState.Finished}`].timestamp).format("DD.MM.YYYY")
                : "",
        });

        if (campaign?.state === CampaignState.Finished) {
            activeStep = steps.length - 1 + 1;
        }

        return { activeStep, steps };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign?.state, JSON.stringify(campaign?.milestones)]);

    return (
        <Paper mb="lg" p="lg" radius="lg">
            <Flex justify="space-between">
                <Text weight={500}>{t("campaign_timeline")}</Text>
                <Group spacing="0">
                    <ActionIcon variant="subtle" onClick={toggleShowIcons}>
                        {showIcons ? <IconEyeOff size="0.8em" /> : <IconEye size="0.8em" />}
                    </ActionIcon>
                </Group>
            </Flex>
            <Stepper
                active={activeStep}
                mt="lg"
                color="gray"
                size="sm"
                iconSize={28}
                styles={{
                    steps: showIcons ? {
                        alignItems: "flex-start",
                    } : undefined,
                    separator: showIcons ? {
                        marginTop: 14,
                    } : undefined,
                    step: {
                        flexDirection: "column",
                    },
                    stepWrapper: {
                        marginBottom: "0.5em",
                    },
                    stepIcon: {
                        backgroundColor: "transparent",
                        border: "none",
                    },
                    stepLabel: {
                        maxWidth: showIcons ? "92px" : undefined,
                        textAlign: showIcons ? "center" : undefined,
                    },
                    stepDescription: {
                        textAlign: showIcons ? "center" : undefined,
                    },
                }}
            >
                {steps.map((step, index) => (
                    <Stepper.Step
                        key={`${index}`}
                        icon={<IconClockPause size="1.3em" style={{ opacity: 0.5 }} />}
                        progressIcon={<IconDots style={{ opacity: 0.5 }} />}
                        completedIcon={<IconCheck size="1.3em" />}
                        withIcon={showIcons}
                        {...step}
                    />
                ))}
            </Stepper>
        </Paper>
    );
};
